import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useClient } from '../../context/ClientContext';
import api from '../../services/api';
import { Review, PaginatedResponse } from '../../types';
import ReviewList from '../reviews/ReviewList';
import DashboardHeader from './DashboardHeader';
import Footer from './Footer';
import './Dashboard.css';

const ClientDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedClientId, activeClients } = useClient();
  
  const [reviews, setReviews] = useState<PaginatedResponse<Review> | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  // Get selected client name - use stable dependencies
  const selectedClientName = useMemo(() => {
    if (!selectedClientId || !activeClients || activeClients.length === 0) {
      return 'Company Dashboard';
    }
    const client = activeClients.find(c => c.id === selectedClientId);
    return client?.client_name || 'Company Dashboard';
  }, [selectedClientId, activeClients.length]);

  const fetchReviews = async () => {
    try {
      setLoading(true);

      if (selectedClientId === null) {
        setLoading(false);
        return;
      }

      const params = new URLSearchParams({
        page: currentPage.toString(),
        per_page: '20',
        client_id: selectedClientId.toString(),
      });

      const response = await api.get<PaginatedResponse<Review>>(
        `/reviews?${params.toString()}`
      );

      setReviews(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
      setLoading(false);
      setReviews(null);
    }
  };

  // Fetch reviews - simple fetch without filters
  useEffect(() => {
    // Don't fetch if no user or no client selected
    if (selectedClientId === null) {
      if (selectedClientId === null && activeClients.length === 0) {
        setLoading(false);
      }
      return;
    }

    fetchReviews();
  }, [currentPage, selectedClientId]);

  // Reset page when client changes
  useEffect(() => {
    if (selectedClientId !== null) {
      setCurrentPage(1);
    }
  }, [selectedClientId]);

  // Handle page change - MEMOIZED and stable
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  return (
    <div className="dashboard">
      <DashboardHeader
        title={selectedClientName}
        showNav
        showEmailButton
      />

      <div className="dashboard-content">
        <div className="reviews-section">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
            <h2 style={{ margin: 0 }}>Your Reviews</h2>
            {selectedClientId !== null && (
              <>
                <a
                  href="/dashboard/opted-emails"
                  onClick={(e) => { e.preventDefault(); navigate('/dashboard/opted-emails'); }}
                  className="btn btn-secondary-orange"
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  Opted Emails
                </a>
                <a
                  href="/dashboard/opted-phones"
                  onClick={(e) => { e.preventDefault(); navigate('/dashboard/opted-phones'); }}
                  className="btn btn-secondary-orange"
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  Opted Phones
                </a>
                <a
                  href="/dashboard/sent-review-emails"
                  onClick={(e) => { e.preventDefault(); navigate('/dashboard/sent-review-emails'); }}
                  className="btn btn-secondary-orange"
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  Sent Review Emails
                </a>
              </>
            )}
          </div>

          {loading && !reviews ? (
            <div className="loading-container">
              <div className="loading">Loading reviews...</div>
            </div>
          ) : reviews && reviews.data && reviews.data.length > 0 ? (
            <ReviewList
              reviews={reviews}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onReviewDeleted={fetchReviews}
              loading={loading}
              clientId={selectedClientId}
            />
          ) : (
            <div className="no-data">No reviews found</div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ClientDashboard;
